import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { TextInputProps } from './TextInput.d'

const TextInput = ({
  placeholder,
  disabled,
  type,
  id,
  label,
  name,
  required,
  variation,
  fullWidth,
  smallerWidthFields,
  value,
  error,
  onChange,
  padding = '',
  onBlur
}: TextInputProps) => {
  const [hasValue, setHasValue] = useState(false)

  // We need this useEffect call to check the initial input state for the form and apply the correct styling.
  // Sometimes autofill values aren't properly handled on load, which leads to the label overlapping the text.
  // This forces the component to check that value and trigger a rerender of the component to ensures the proper styling.
  useEffect(() => {
    // The !! forces this to a boolean for our state value
    setHasValue(!!value)
  }, [value])

  const labelClassNames = classNames({
    hidden: !label && variation !== 'default',
    'font-sansPro font-normal text-left text-secondary tracking-[0.2px] pointer-events-none h-[24px] mb-[3px]  whitespace-pre overflow-hidden':
      true,
    'text-base leading-[20px]': label && variation === 'default' && !hasValue,
    'absolute top-[15px] left-[10px] right-[10px] peer-focus/input:text-[10px] peer-focus/input:leading-[10px] peer-focus/input:top-[10px]':
      label && variation === 'default' && padding === 'large' && !hasValue,
    'absolute top-[12px] left-4 right-4 peer-focus/input:text-[10px] peer-focus/input:leading-[10px] peer-focus/input:top-[10px]':
      label && variation === 'default' && padding === 'medium' && !hasValue,
    'absolute left-[10px] right-[10px] text-[10px] leading-[10px] top-[10px]':
      label && variation === 'default' && padding === 'large' && hasValue
  })

  const inputClassNames = classNames(
    'bg-white w-full placeholder:text-gray-50 text-default rounded-[4px] font-sansPro focus-within:pt-[18px] focus-within:pb-[6px] pt-[18px] pb-[6px]',
    {
      // Full Width
      '!ml-0': smallerWidthFields && fullWidth,
      // Border shadow
      'border border-gray-50 shadow-none text-base box-border peer/input':
        variation === 'default' && !error,
      'placeholder-transparent': variation === 'default' && label !== '',
      'border border-otl-error border-error': error,
      'rounded-none': variation === 'otl-default' && error,

      // Padding
      'px-2 py-[9px]': padding !== '' && variation !== 'default',
      'py-3 px-[10px] max-h-[50px]':
        padding === 'large' && variation === 'default',
      'py-3 px-4 max-h-[48px]': padding === 'medium' && variation === 'default'
    }
  )

  const errorClassNames = classNames(
    'flex flex-row font-sansPro items-center absolute bottom-[-16px] !text-[12px] text-error text-sm left-0 leading-4 mt-2 pl-[2px] text-[#f98699]'
  )

  return (
    <>
      <input
        aria-label={label || placeholder || name}
        className={inputClassNames}
        disabled={disabled}
        id={id}
        maxLength={255}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        placeholder={!label ? placeholder : null}
        required={required}
        type={type}
        value={value}
      />
      {label ? (
        <label className={labelClassNames} htmlFor={id}>
          {label}
        </label>
      ) : null}
      {error ? (
        <span className={errorClassNames} role='alert'>
          {error}
        </span>
      ) : null}
    </>
  )
}

export default TextInput
